import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { FaMapMarkerAlt } from "react-icons/fa";
import { createPortal } from 'react-dom';
import profile_api from '../../api-services/profile/profile.services';
import { SITELINK } from '../../app-routes/Links';
import GlobalLayout from '../../layouts/GlobalLayout';
import LazyLoad from 'react-lazyload';
import { formatDateOnly, getFirstLetters, getUser } from '../../app-utils';
import { Loader, TelegramImage, WeChatImage, WhatsappImage } from '../../assets/custom-icons';
import TopBar from '../../layouts/~includes/TopBar';
import { ServiceBanner, CatapotLogo } from '../../assets/images';
import { SkeletonFeed } from '../../components/skeletons';
import PostCard from '../home/home-components/PostCard';
import { useInView } from 'react-intersection-observer';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { PostInterface } from '../../app-schemas/posts/post.schema';
import { fetchUserReviews } from '../../api-services/ratings/useFetchUserReview';
import { fetchUserTweets } from '../../api-services/feeds/useFetchUserTweets';
import { fetchUserServicesFeeds } from '../../api-services/feeds/useFetchUserServicePosts';
import { updateReview } from '../../api-services/ratings/useUpdateReview';
import { deleteReview } from '../../api-services/ratings/useDeleteReview';
import { fetchUserAverageRating } from '../../api-services/ratings/useFetchAverageUserRating';
import { useTheme } from '../../context/theme-context';

// Rename updateReview to editReview for consistency
const editReview = updateReview;

const Posts = ({ userId }: { userId: string }) => {
  const { ref, inView } = useInView();
  const { isDarkMode } = useTheme();

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useInfiniteQuery({
    queryKey: ['text-feeds', userId],
    queryFn: ({ pageParam }) => fetchUserTweets({ userId, pageParam }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.currentPage < lastPage.totalPages ? lastPage.currentPage + 1 : undefined,
  });

  const content = useMemo(() =>
    data?.pages.flatMap((page) =>
      page.data.map((post: PostInterface, index: number) => (
        <PostCard
          key={post.post_id}
          index={post.post_id}
          post={post}
          innerRef={page.data.length - 2 === index + 1 ? ref : undefined}
        />
      )),
    ),
    [data, ref],
  );

  useEffect(() => {
    if (inView && hasNextPage) fetchNextPage();
  }, [inView, hasNextPage, fetchNextPage]);

  const hasNoPosts = !isLoading && data?.pages[0]?.data.length === 0;

  if (isLoading) {
    return (
      <div className="p-3">
        <SkeletonFeed count={3} variant="post" />
      </div>
    );
  }

  if (hasNoPosts) {
    return (
      <div className="p-3">
        <div className="flex flex-col justify-center items-center py-20 gap-2">
          <p className={`text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No posts available</p>
          <p className={`text-sm text-center ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Be the first to create a post!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3">
      <div className="text-sm">{content}</div>
      {isFetchingNextPage && (
        <div className="flex py-5 justify-center">
          <img 
            src={Loader} 
            alt="loader" 
            className="h-[20px] w-[20px] animate-spin"
            style={{ filter: isDarkMode ? 'invert(1)' : 'none' }}
          />
        </div>
      )}
      {!hasNextPage && data?.pages[0]?.data.length > 0 && (
        <div className="text-xs flex justify-center py-5 text-gray-600 dark:text-gray-400">
          No more content
        </div>
      )}
    </div>
  );
};

const Services = ({ userId }: { userId: string }) => {
  const { ref, inView } = useInView();
  const { isDarkMode } = useTheme();

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useInfiniteQuery({
    queryKey: ['service-feeds', userId],
    queryFn: ({ pageParam }) => fetchUserServicesFeeds({ userId, pageParam }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.currentPage < lastPage.totalPages ? lastPage.currentPage + 1 : undefined,
  });

  const content = useMemo(() =>
    data?.pages.flatMap((page) =>
      page.data.map((post: PostInterface, index: number) => (
        <PostCard
          key={post.post_id}
          index={post.post_id}
          post={post}
          innerRef={page.data.length - 2 === index + 1 ? ref : undefined}
        />
      )),
    ),
    [data, ref],
  );

  useEffect(() => {
    if (inView && hasNextPage) fetchNextPage();
  }, [inView, hasNextPage, fetchNextPage]);

  const hasNoPosts = !isLoading && data?.pages[0]?.data.length === 0;

  if (isLoading) {
    return (
      <div className="p-3">
        <SkeletonFeed count={3} variant="post" />
      </div>
    );
  }

  if (hasNoPosts) {
    return (
      <div className="p-3">
        <div className="mx-auto size-[300px]">
          <img src={ServiceBanner} alt="service banner" className="w-full h-auto" />
        </div>
        <div className="flex flex-col justify-center items-center py-10 gap-2">
          <p className={`text-center text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No services available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3">
      <div className="text-sm">{content}</div>
      {isFetchingNextPage && (
        <div className="flex py-5 justify-center">
          <img 
            src={Loader} 
            alt="loader" 
            className="h-[20px] w-[20px] animate-spin"
            style={{ filter: isDarkMode ? 'invert(1)' : 'none' }}
          />
        </div>
      )}
      {!hasNextPage && data?.pages[0]?.data.length > 0 && (
        <div className="text-xs flex justify-center py-5 text-gray-600 dark:text-gray-400">
          No more content
        </div>
      )}
    </div>
  );
};

// Delete Confirmation Modal Component (using Portal)
const DeleteConfirmModal = ({ isOpen, onConfirm, onCancel, isSubmitting }: { isOpen: boolean; onConfirm: () => void; onCancel: () => void; isSubmitting: boolean }) => {
  
  const { isDarkMode } = useTheme();
  
  if (!isOpen) return null;
  
  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]">
      <div className={`rounded-lg p-6 max-w-sm mx-4 ${isDarkMode ? 'bg-neutral-800' : 'bg-white'}`}>
        <h3 className={`text-base font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Delete Review</h3>
        <p className={`text-xs mb-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          Are you sure you want to delete this review? This action cannot be undone.
        </p>
        <div className="flex gap-3 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isSubmitting}
            className="px-4 py-2 text-xs bg-red-500 text-white rounded hover:bg-red-600 transition-colors disabled:opacity-50"
          >
            {isSubmitting ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Review Card Component
const ReviewCard = ({ review, isOwner, onEdit, onDelete }: {  review: any;  isOwner: boolean; 
  onEdit: (reviewId: number, newComment: string, newRating: number) => Promise<void>;
  onDelete: (reviewId: number) => Promise<void>;
}) => {
  const { isDarkMode } = useTheme();
  const [isEditing, setIsEditing] = useState(false);
  const [editComment, setEditComment] = useState(review.comment);
  const [editRating, setEditRating] = useState(review.rating);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleEdit = async () => {
    if (editComment.trim() === '') return;
    setIsSubmitting(true);
    try {
      await onEdit(review.id, editComment, editRating);
      setIsEditing(false);
    } catch (error) {
      console.error('Error editing review:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setIsSubmitting(true);
    try {
      await onDelete(review.id);
      setShowDeleteConfirm(false);
    } catch (error) {
      console.error('Error deleting review:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditComment(review.comment);
    setEditRating(review.rating);
    setShowDeleteConfirm(false);
  };

  return (
    <>
      <div className={`flex gap-3 items-start border-b last:border-b-0 py-3 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="shrink-0">
          {review.reviewer_profile_picture ? (
            <LazyLoad height={300} offset={1000} once>
              <img
                src={review.reviewer_profile_picture}
                alt={review.reviewer}
                className="w-[40px] h-[40px] rounded-full object-cover"
              />
            </LazyLoad>
          ) : (
            <span className="flex justify-center items-center w-[40px] h-[40px] bg-[#223f64] rounded-full">
              <h3 className="text-white text-lg uppercase font-[600]">
                {getFirstLetters(review.reviewer?.split(' ')[0] || 'U')}
              </h3>
            </span>
          )}
        </div>

        <div className="flex-1">
          <div className="flex items-start justify-between w-full gap-2">
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-sm font-semibold madimi-one ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {review.reviewer}
                </span>
                <span className={`text-xs font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  @{review.reviewer_username}
                </span>
                
                {/* Edited indicator */}
                {review.is_updated && !isEditing && (
                  <span className={`text-[10px] italic ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    (Edited)
                  </span>
                )}
              </div>
              
              <div className="mt-0.5">
                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {review.timestamp}
                  {review.is_updated && review.updated_at && !isEditing && (
                    <span className="ml-1 text-[10px] opacity-75">
                      · updated {review.updated_at}
                    </span>
                  )}
                </p>
              </div>
            </div>
            
            {/* Edit and Delete buttons - Far right */}
            {isOwner && !isEditing && (
              <div className="flex gap-1 shrink-0">
                <button
                  onClick={() => setIsEditing(true)}
                  className={`p-1 rounded transition-colors cursor-pointer ${isDarkMode ? 'hover:bg-neutral-700' : 'hover:bg-gray-100'}`}
                  disabled={isSubmitting}
                  aria-label="Edit review"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
                    <path d="M17 3l4 4-7 7H10v-4l7-7z" />
                    <path d="M4 20h16" />
                  </svg>
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className={`p-1 rounded transition-colors ${isDarkMode ? 'hover:bg-neutral-700' : 'hover:bg-gray-100'}`}
                  disabled={isSubmitting}
                  aria-label="Delete review"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-red-500">
                    <path d="M4 7h16" />
                    <path d="M10 11v6" />
                    <path d="M14 11v6" />
                    <path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2l1-12" />
                    <path d="M9 3h6" />
                  </svg>
                </button>
              </div>
            )}
          </div>

          <div className="mt-1 pb-2 w-full">
            {isEditing ? (
              <div className="space-y-2">
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      onClick={() => setEditRating(star)}
                      className="focus:outline-none"
                      type="button"
                      aria-label={`Rate ${star} stars`}
                    >
                      <span className={`text-xl ${star <= editRating ? 'text-yellow-400' : 'text-gray-300'}`}>
                        ★
                      </span>
                    </button>
                  ))}
                </div>

                <textarea
                  value={editComment}
                  onChange={(e) => setEditComment(e.target.value)}
                  className={`w-full p-2 text-sm border rounded-md ${isDarkMode ? 'bg-neutral-700 border-neutral-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                  placeholder="Edit your review..."
                  rows={3}
                />

                <div className="flex gap-2">
                  <button
                    onClick={handleEdit}
                    disabled={isSubmitting || !editComment.trim()}
                    className="flex items-center gap-1 px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    {isSubmitting ? 'Updating...' : 'Update'}
                  </button>
                  <button
                    onClick={handleCancel}
                    disabled={isSubmitting}
                    className="flex items-center gap-1 px-3 py-1 text-sm bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                <p className={`text-[12px] ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{review.comment}</p>
                <div className="flex mt-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <span 
                      key={star} 
                      className={`text-xl ml-0.5 ${star <= review.rating ? 'text-yellow-400' : 'text-gray-300'}`}
                    >
                      ★
                    </span>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <DeleteConfirmModal
        isOpen={showDeleteConfirm}
        onConfirm={handleDelete}
        onCancel={handleCancel}
        isSubmitting={isSubmitting}
      />
    </>
  );
};

// Main Reviews Component
const Reviews = ({ me, user }: { me: any; user: any }) => {
  const { ref, inView } = useInView();
  const { isDarkMode } = useTheme();
  const queryClient = useQueryClient();
  const [isMutating, setIsMutating] = useState(false);

  const userLoggedIn = user.id === me.id;

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, refetch } = useInfiniteQuery({
    queryKey: ['reviews-feeds', me.id],
    queryFn: ({ pageParam }) => fetchUserReviews({ userId: me.id, pageParam }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.currentPage < lastPage.totalPages ? lastPage.currentPage + 1 : undefined,
  });

  const handleEditReview = useCallback(async (reviewId: number, comment: string, rating: number) => {
    setIsMutating(true);
    try {
      await editReview(reviewId, { comment, rating });
      
      queryClient.setQueryData(['reviews-feeds', me.id], (oldData: any) => {
        if (!oldData) return oldData;
        
        return {
          ...oldData,
          pages: oldData.pages.map((page: any) => ({
            ...page,
            data: page.data.map((review: any) =>
              review.id === reviewId ? { ...review, comment, rating, is_updated: true, updated_at: 'Just now' } : review
            ),
          })),
        };
      });
      
      await refetch();
    } catch (error) {
      console.error('Error editing review:', error);
      throw error;
    } finally {
      setIsMutating(false);
    }
  }, [me.id, queryClient, refetch]);

  const handleDeleteReview = useCallback(async (reviewId: number) => {
    setIsMutating(true);
    try {
      await deleteReview(reviewId);
      
      queryClient.setQueryData(['reviews-feeds', me.id], (oldData: any) => {
        if (!oldData) return oldData;
        
        return {
          ...oldData,
          pages: oldData.pages.map((page: any) => ({
            ...page,
            data: page.data.filter((review: any) => review.id !== reviewId),
          })),
        };
      });
      
      await refetch();
    } catch (error) {
      console.error('Error deleting review:', error);
      throw error;
    } finally {
      setIsMutating(false);
    }
  }, [me.id, queryClient, refetch]);

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && !isMutating) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, fetchNextPage, isFetchingNextPage, isMutating]);

  const content = useMemo(() => {
    if (!data?.pages) return null;
    
    const allReviews = data.pages.flatMap((page) => page.data);
    
    if (allReviews.length === 0) return null;
    
    return allReviews.map((review: any, index: number) => {
      const isOwner = review.reviewer_username === user.username;
      const isLastItem = index === allReviews.length - 1;
      
      return (
        <div key={review.id} ref={isLastItem ? ref : undefined}>
          <ReviewCard
            review={review}
            isOwner={isOwner}
            onEdit={handleEditReview}
            onDelete={handleDeleteReview}
          />
        </div>
      );
    });
  }, [data, ref, handleEditReview, handleDeleteReview, user.username]);

  const hasNoReviews = !isLoading && (!data?.pages[0]?.data || data.pages[0].data.length === 0);

  if (isLoading) {
    return (
      <div className="p-3">
        <SkeletonFeed count={3} variant="post" />
      </div>
    );
  }

  if (hasNoReviews) {
    return (
      <div className="p-3">
        <div className="mx-auto size-[300px]">
          <img src={ServiceBanner} alt="service banner" className="w-full h-auto" />
        </div>
        <div className="flex flex-col justify-center items-center py-10 gap-2">
          <p className={`text-center text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No reviews available</p>
          {!userLoggedIn && (
            <Link to={`${SITELINK.MY_PROFILE}/${me.username}/reviews`} className="text-sm text-blue-400 font-semibold hover:underline">
              Write a review
            </Link>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="p-3">
      {!userLoggedIn && (
        <Link to={`${SITELINK.MY_PROFILE}/${me.username}/reviews`} className="text-sm text-blue-400 font-semibold hover:underline inline-block mb-3">
          Write a review
        </Link>
      )}
      <div className="text-sm">{content}</div>
      {isFetchingNextPage && (
        <div className="flex py-5 justify-center">
          <img 
            src={Loader} 
            alt="loader" 
            className="h-[20px] w-[20px] animate-spin"
            style={{ filter: isDarkMode ? 'invert(1)' : 'none' }}
          />
        </div>
      )}
      {!hasNextPage && data?.pages[0]?.data.length > 0 && (
        <div className="text-xs flex justify-center py-5 text-gray-600 dark:text-gray-400">
          No more reviews
        </div>
      )}
    </div>
  );
};
  
// Main Component UI
const ViewProfile = () => {
  document.body.classList.remove('overflow-hidden');

  const { userID } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const user: any = getUser();
  const [me, setMe] = useState<any>({});
  const [pageLoading, setPageLoading] = useState<boolean>(false);
  const { isDarkMode } = useTheme();

  const { data, isLoading } = useQuery({
    queryKey: ['average-rating', me.id],
    queryFn: () => fetchUserAverageRating(me.id)
  })

  const averageRating = data as { totalReviews: number; averageRating: number; };

  // Initialize activeTab from URL if it's 'reviews', otherwise default to 'posts'
  const [activeTab, setActiveTab] = useState<string>(() => {
    const tab = searchParams.get('activeTab');
    return tab === 'reviews' ? 'reviews' : 'posts';
  });

  // Listen for URL changes
  useEffect(() => {
    const tab = searchParams.get('activeTab');
    if (tab === 'reviews') {
      setActiveTab('reviews');
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    myProfile(userID ?? '');
  }, [userID]);

  const myProfile = async (userID: string) => {
    setPageLoading(true);
    const my_profile: any = await profile_api.ProfileById(userID);
    setMe(my_profile.data.data);
    setPageLoading(false);
  };

  const chatOnWhatsapp = (number: number | null) => {
    const url = `https://wa.me/${number}`;
    window.open(url, '_blank');
  };

  const handleTelegramClick = (number: number | null) => {
    const url = `tg://openchat?phone=${number}`;
    window.open(url, '_blank');
  };

  const renderTabContent = () => {
    switch(activeTab) {
      case 'posts':
        return <Posts userId={me.id} />;
      case 'services':
        return <Services userId={me.id} />;
      case 'reviews':
        return <Reviews me={me} user={user} />;
      default:
        return <Posts userId={me.id} />;
    }
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    if (window.location.search) {
      setSearchParams({}, { replace: true });
    }
  };

  // Helper function to render stars
  const renderStars = (rating: number) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
    
    return (
      <div className="flex items-center gap-0.5">
        {[...Array(fullStars)].map((_, i) => (
          <svg key={`full-${i}`} className="w-3.5 h-3.5 text-yellow-400 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
            <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z"/>
          </svg>
        ))}
        {hasHalfStar && (
          <svg className="w-3.5 h-3.5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0v15z"/>
          </svg>
        )}
        {[...Array(emptyStars)].map((_, i) => (
          <svg key={`empty-${i}`} className="w-3.5 h-3.5 text-gray-300 dark:text-gray-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z"/>
          </svg>
        ))}
      </div>
    );
  };

  return (
    <GlobalLayout title="Catapot | My Profile">
      <section className={`min-h-screen ${isDarkMode ? 'bg-neutral-800' : 'bg-[#E1F0F8F7]'}`}>
        <div className="fixed top-0 w-full z-50">
          <TopBar title="Profile" goBack={true} />
        </div>

        {pageLoading ? (
          <div className="mt-[48px]">
            <SkeletonFeed count={3} variant="post" />
          </div>
        ) : (
          <div className="mt-[48px]">
            <div className="bg-white dark:bg-neutral-800 flex gap-3 justify-start py-3 px-3 pb-3">
              <div className="overflow-hidden flex justify-center">
                <div className="relative">
                  {me?.profile_picture_url ? (
                    <LazyLoad height={300} offset={1000} once>
                      <img
                        alt="me"
                        src={me?.profile_picture_url}
                        className="border-[1px] object-cover rounded-full w-[50px] h-[50px] bg-gray-200 shadow-sm overflow-hidden"
                      />
                    </LazyLoad>
                  ) : (
                    <span className="flex justify-center items-center w-[50px] h-[50px] bg-[#223f64] rounded-full border border-green-100">
                      <h3 className="text-white text-3xl uppercase font-[600]">
                        {getFirstLetters(me?.first_name ?? 'C')}
                      </h3>
                    </span>
                  )}
                </div>
              </div>

              <div className="mt-2 flex-1">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className={`font-semibold text-lg mb-0 madimi-one ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {me?.username ?? user?.username}
                    </h3>
                    
                    {/* Rating and Reviews Display */}
                    {!isLoading && averageRating && averageRating.totalReviews > 0 && (
                      <div className="flex items-center gap-2 my-1">
                        {renderStars(averageRating.averageRating)}
                        <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          {averageRating.averageRating.toFixed(1)}
                        </span>
                        <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          ({averageRating.totalReviews} {averageRating.totalReviews === 1 ? 'review' : 'reviews'})
                        </span>
                      </div>
                    )}
                    
                    {!isLoading && averageRating && averageRating.totalReviews === 0 && (
                      <div className="flex items-center gap-2 my-1">
                        <div className="flex items-center gap-0.5">
                          {[...Array(5)].map((_, i) => (
                            <svg key={i} className="w-3.5 h-3.5 text-gray-300 dark:text-gray-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                              <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z"/>
                            </svg>
                          ))}
                        </div>
                        <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No reviews yet</span>
                      </div>
                    )}
                  </div>

                  {user.username === me?.username && (
                    <div className='w-fit'>
                      <Link
                        to={SITELINK.EDIT_PROFILE}
                        className="bg-custom-600 text-white font-medium border dark:border-custom-600 border-custom-primary px-5 py-3 rounded-full text-xs w-fit"
                      >
                        Edit Profile
                      </Link>
                    </div>
                  )}
                </div>
                <div className=''>
                  <p className={`text-xs text-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>{me?.first_name} {me?.last_name}</p>
                  <p className={`text-xs text-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>{me?.email}</p>
                  <div className='text-xs text-custom-600 flex items-center gap-2 py-1.5 px-3 rounded-full w-fit mt-1 bg-gray-100 dark:bg-gray-700'>
                    <FaMapMarkerAlt className='text-[0.8rem]' />
                    {me?.residency_city}
                  </div>
                  <div className={`text-[11px] mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Joined on {formatDateOnly(me?.joined_date)}.
                  </div>
                </div>
              </div>
            </div>

            {/* Start a conversation section */}
            {user.username !== me?.username && (
              <div className="w-full bg-white dark:bg-neutral-800 py-4 px-4 border-t border-b border-gray-200 dark:border-gray-700">
                <label className={`text-xs font-medium mb-2 flex items-center gap-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                  </svg>
                  Start a conversation
                </label>

                <div className="flex items-center justify-between gap-2 mt-3">
                  <button
                    onClick={() => chatOnWhatsapp(me?.whatsapp_number)}
                    className="flex-1 flex flex-col items-center gap-2 p-3 rounded-xl bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30 transition-all duration-200 group"
                  >
                    <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-200">
                      <img src={WhatsappImage} alt="WhatsApp" className="w-5 h-5" />
                    </div>
                    <span className="text-xs font-medium text-green-600 dark:text-green-400">WhatsApp</span>
                  </button>

                  <button
                    onClick={() => handleTelegramClick(me?.phone_number)}
                    className="flex-1 flex flex-col items-center gap-2 p-3 rounded-xl bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-all duration-200 group"
                  >
                    <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-200">
                      <img src={TelegramImage} alt="Telegram" className="w-5 h-5" />
                    </div>
                    <span className="text-xs font-medium text-blue-600 dark:text-blue-400">Telegram</span>
                  </button>

                  <button
                    className="flex-1 flex flex-col items-center gap-2 p-3 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 transition-all duration-200 group"
                  >
                    <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-200">
                      <img src={WeChatImage} alt="WeChat" className="w-5 h-5" />
                    </div>
                    <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400">WeChat</span>
                  </button>

                  <Link
                    to={SITELINK.CHATIN}
                    onClick={() => {
                      localStorage.setItem(
                        'lastChattedUserInfo',
                        JSON.stringify({
                          id: me?.user_id,
                          name: me?.username,
                          image: me?.profile_picture,
                        }),
                      );
                    }}
                    className="flex-1 flex flex-col items-center gap-2 p-3 rounded-xl bg-custom-600/10 dark:bg-custom-600/20 hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-all duration-200 group"
                  >
                    <div className="w-10 h-10 rounded-full bg-custom-600 flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-200">
                      <img src={CatapotLogo} alt="Message" className="size-8 object-cover" />
                    </div>
                    <span className="text-xs font-medium text-custom-600 dark:text-custom-600">Catapot</span>
                  </Link>
                </div>
              </div>
            )}

            <div className="flex border-b w-full mt-3 dark:border-gray-700">
              <button
                onClick={() => handleTabChange('posts')}
                className={`flex-1 pb-1 text-xs font-medium relative transition-colors duration-200 ${
                  activeTab === 'posts' 
                    ? 'text-custom-600' 
                    : `text-gray-500 hover:text-gray-700 ${isDarkMode ? 'dark:text-gray-400 dark:hover:text-gray-300' : ''}`
                }`}
              >
                Posts
                {activeTab === 'posts' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-custom-600"></div>
                )}
              </button>
              
              <button
                onClick={() => handleTabChange('services')}
                className={`flex-1 pb-1 text-xs font-medium relative transition-colors duration-200 ${
                  activeTab === 'services' 
                    ? 'text-custom-600' 
                    : `text-gray-500 hover:text-gray-700 ${isDarkMode ? 'dark:text-gray-400 dark:hover:text-gray-300' : ''}`
                }`}
              >
                Services
                {activeTab === 'services' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-custom-600"></div>
                )}
              </button>
              
              <button
                onClick={() => handleTabChange('reviews')}
                className={`flex-1 pb-1 text-xs font-medium relative transition-colors duration-200 ${
                  activeTab === 'reviews' 
                    ? 'text-custom-600' 
                    : `text-gray-500 hover:text-gray-700 ${isDarkMode ? 'dark:text-gray-400 dark:hover:text-gray-300' : ''}`
                }`}
              >
                Reviews
                {activeTab === 'reviews' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-custom-600"></div>
                )}
              </button>
            </div>

            <div className="min-h-[200px]">
              {renderTabContent()}
            </div>
          </div>
        )}
      </section>
    </GlobalLayout>
  );
};

export default ViewProfile;